import {
  type AFConsentOptions,
  type AFCuid,
  type AFEvent,
  type AFInit,
  AppsFlyer,
} from 'appsflyer-capacitor-plugin';
import { isAndroid, isIOS, isWeb } from '@/domains/shared/utils/native';

/**
 * DMA / Google Ads consent signals for AppsFlyer.
 * Must be set via setConsentDataV2 before initSDK — GTM Consent Mode does not
 * propagate into the AppsFlyer SDK.
 *
 * Maps to Google Consent Mode:
 * - hasConsentForDataUsage → ad_user_data
 * - hasConsentForAdsPersonalization → ad_personalization
 * - hasConsentForAdStorage → ad_storage
 *
 * Values mirror native GTM grants in googleTagManager.tsx.
 */
export const APPSFLYER_DMA_CONSENT: AFConsentOptions = {
  isUserSubjectToGDPR: true,
  hasConsentForDataUsage: true,
  hasConsentForAdsPersonalization: true,
  hasConsentForAdStorage: true,
};

const getAFConfigForCurrentPlatform = (): AFInit | null => {
  if (isWeb()) return null;

  const androidPlatform = isAndroid();
  const iosPlatform = isIOS();
  if (!iosPlatform && !androidPlatform) return null;

  const platformEnviroment = process.env.NEXT_PUBLIC_PLATFORM_APP_ENV || 'dev';
  const isProduction = platformEnviroment === 'production';
  const devKey = process.env.NEXT_PUBLIC_APPSFLYER_DEV_KEY || '';
  const appID =
    (iosPlatform
      ? process.env.NEXT_PUBLIC_APPSFLYER_IOS_APP_ID
      : process.env.NEXT_PUBLIC_APPSFLYER_ANDROID_APP_ID) || '';

  if (!devKey || !appID) {
    console.warn(
      'AppsFlyer: Missing required configuration. devKey:',
      !!devKey,
      'appID:',
      !!appID,
    );
    return null;
  }

  const config: AFInit = {
    devKey,
    appID,
    // isDebug: !isProduction,
    isDebug: true,
    minTimeBetweenSessions: 6,
    registerOnDeepLink: true,
    registerConversionListener: true,
    registerOnAppOpenAttribution: true,
    deepLinkTimeout: 4000,
  };

  if (iosPlatform) {
    config.waitForATTUserAuthorization = 60;
    config.useUninstallSandbox = !isProduction;
  }

  return config;
};

const setAppsFlyerDmaConsent = async (): Promise<void> => {
  try {
    await AppsFlyer.setConsentDataV2(APPSFLYER_DMA_CONSENT);
    console.log('AppsFlyer DMA consent set');
  } catch (err: unknown) {
    console.error('AppsFlyer setConsentDataV2 failed', err);
  }
};

export const initAppsFlyer = async (): Promise<void> => {
  const afConfig = getAFConfigForCurrentPlatform();
  if (!afConfig) return;

  try {
    // Required before init for Google Ads / DMA partner sharing
    await setAppsFlyerDmaConsent();
    await AppsFlyer.initSDK(afConfig);
    console.log('AppsFlyer initialised');
  } catch (err: unknown) {
    console.error('AppsFlyer init failed', err);
  }
};

export const inAppConversionEvent = async (
  conversionEvent: string,
  value?: number,
  currency: string = 'GBP',
): Promise<AFEvent> => {
  if (isWeb()) return {} as AFEvent;

  const data: AFEvent = {
    eventName: conversionEvent,
    eventValue:
      value != null ? { af_revenue: value, af_currency: currency } : {},
  };

  try {
    const result = await AppsFlyer.logEvent(data);
    console.log('AppsFlyer event logged:', result.res);
  } catch (err) {
    console.error('AppsFlyer logEvent error:', err);
  }

  return data;
};

export const appsFlyerSetUserId = async (userId: string): Promise<void> => {
  if (isWeb()) return;

  const cuid: AFCuid = { cuid: userId };

  try {
    await AppsFlyer.setCustomerUserId(cuid);
  } catch (err) {
    console.error('AppsFlyer appsFlyerSetUserId error:', err);
  }
};
